import { Box, Dialog, DialogContent, DialogTitle, Grid } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { useCallback, useEffect } from 'react';
import {
  updatePrice,
  closeErrorDialog,
  removeItemFromErrorList,
} from '../../../../redux/slices/orders';
import { useDispatch, useSelector } from '../../../../redux/store';
import { CART_CHECK } from '../../../../reusable-components/datagrid/pendingOrderColumns';
import ActionButtons from './ActionButtons';
import CustomDataGrid from '../../../../reusable-components/datagrid/CustomDataGrid';
import { AwaitButton } from '../../../../reusable-components/await-button';
import axiosInstance from '../../../../utils/axios';
import { objectToFormData } from '../../../../api/utils';
import CloseIconButton from '../../../../reusable-components/basic-buttons/CloseIconButton';

CartCheck.propTypes = {
  updatedCartItems: PropTypes.array,
  setUpdatedCartItems: PropTypes.func, 
  setData: PropTypes.func,
};

export default function CartCheck({ updatedCartItems, setUpdatedCartItems, setData }) {
  const { id = '' } = useParams();

  const dispatch = useDispatch();

  const { orderById, cartCheckItemsById, openErrorDialog } = useSelector(
    (state) => state.orders
  );
  const { items = [], facilityId } = orderById[id] || {};

  const handleMakeItemsPPD = async () => {
    const items = (updatedCartItems ?? [])
      .filter((i) => i.errors.some((i) => i.includes('PPD mismatch')))
      .map((i) => ({ id: i.id, itemId: i.itemId, isPPD: !i.isPPD }));  
    axiosInstance.post('/SetItemsPPD', objectToFormData({ input: items, facilityId }))
      .then(() => { items.forEach((item) => dispatch(removeItemFromErrorList(id, item.id))); })
      .catch(() => enqueueSnackbar("Failed to update items", { variant: "error" })) 
  };

  useEffect(() => {
    const mergedItems = items
      ?.map((item) => {
        const cartCheckItem = cartCheckItemsById[id]?.find((cci) => cci.id === item.id);
        let merged = {
          ...item,
        };
        if (cartCheckItem) {
          if (cartCheckItem.errors && cartCheckItem.errors?.length > 0) {
            merged = {
              ...merged,
              id: item.id,
              errors: cartCheckItem.errors,
              itemDirectReplacements: cartCheckItem.itemDirectReplacements || [],
              itemSubstitutes: cartCheckItem.itemSubstitutes || [],
              updatedPrice: cartCheckItem.price > 0 ? cartCheckItem.price : item.price,
            };
          }
        }
        return merged;
      })
      .filter((mergedItem) => {
        const originalItem = items?.find((item) => item.id === mergedItem.id);
        return (
          (mergedItem.errors && mergedItem.errors?.length > 0) ||
          (originalItem && mergedItem.price !== originalItem.price)
        );
      });

    setUpdatedCartItems(mergedItems);
  }, [items, cartCheckItemsById]);

  const CustomLeftToolbar = useCallback(
    () => (
      <Grid container direction="row" justifyContent="flex-start" alignItems="flex-end" spacing={1}>
        {updatedCartItems.filter((item) => item.price > 0 && item.updatedPrice !== item.price)?.length > 0 && (
          <Grid item>
            <AwaitButton
              variant="contained"
              size="small"
              onClick={async () => {
                dispatch(updatePrice(
                  id,
                  facilityId,
                  updatedCartItems.filter((item) => (item.price > 0 || item.itemId === -1) && item.updatedPrice !== item.price)
                ));
              }}
            >
              Update All Prices
            </AwaitButton>
          </Grid>
        )}
        {updatedCartItems?.filter((i) => i.errors.some((i) => i.includes('PPD mismatch')))?.length > 0 && (
          <Grid item>
            <AwaitButton variant="contained" size="small" onClick={handleMakeItemsPPD}>
              Update PPD
            </AwaitButton>
          </Grid>
        )}
      </Grid>
    ), [dispatch, facilityId, updatedCartItems]);

  const actions = {
    field: 'actions',
    headerName: 'Actions',
    disableReorder: true,
    flex: 0.5,
    renderCell: (params) => <ActionButtons row={params.row} setUpdatedCartItems={setUpdatedCartItems} setData={setData}/>,
    valueGetter: (params) => params.row?.itemSubstitutes?.length > 0 ? 0 : 1,
    id: 'actions',
    className: 'actions',
  };

  return (
    <>
      <Dialog
        open={openErrorDialog /*&& updatedCartItems?.length !== 0*/}
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          dispatch(closeErrorDialog());
        }}
        maxWidth="full"
        fullWidth
      >
        <DialogTitle
          sx={{
            textAlign: 'center',
            mt: 2,
          }}
        >
          Item Errors
          <CloseIconButton onClick={() => dispatch(closeErrorDialog())}/>
        </DialogTitle>
        <>
          <DialogContent>
            <Box sx={{ width: '100%', minHeight: '36vh' }}>
              <CustomDataGrid
                gridId="orders-cart-check"
                boxSX={{ height: 'calc(100vh - 340px)' }}
                data={updatedCartItems}
                gridColumns={[...CART_CHECK, actions]}
                sort={[{ field: 'facility', sort: 'asc' }]}
                CustomLeftToolbar={CustomLeftToolbar}
                isModal
                getRowClassName={(params) => params.row.deleted ? 'disabled allow-click' : ''}
              />
            </Box>
          </DialogContent>{' '}
        </>
      </Dialog>
    </>
  );
}
